import type { ServerPageData } from '@/features/product-editor/types';
import { PRODUCT_TYPES, StoreType } from '@/lib/constants';

export type StoreType = (typeof StoreType)[keyof typeof StoreType];

export type ProductType = (typeof PRODUCT_TYPES)[keyof typeof PRODUCT_TYPES];

export type PageType = ProductType;

export enum StoreTheme {
    DEFAULT = 'default',
    MODERNO = 'moderno',
    KELS = 'kels',
    TYLA = 'tyla',
    STONE = 'stone',
    MINIMA = 'minima',
    NIGHTVIEW = 'nightview',
    MATERIAL = 'material',
    SPOTLIGHT = 'spotlight',
    ECLIPSE = 'eclipse',
    TRISH = 'trish',
}

export enum PageStatus {
    DOES_NOT_EXIST = -1,
    DELETED = 0,
    DRAFT = 1,
    PUBLISHED = 2,
}

export enum ModuleStatus {
    DELETED = 0,
    PUBLISHED = 1,
    DRAFT = 2,
    DRIP = 3,
}

export enum BookingType {
    ONE_ON_ONE = 'one-on-one',
    GROUP = 'group',
    WEBINAR = 'webinar',
}

export enum PaymentInterval {
    MONTHLY = 'month',
    YEARLY = 'year',
    WEEKLY = 'week',
    DAILY = 'day',
}

export const PAGE_LOCATIONS = {
    STORE: 'store',
    LANDING: 'landing',
} as const;

export enum PageLocation {
    STORE = 'store',
    LANDING = 'landing',
}

export enum DigitalAssetType {
    FILE = 'file',
    LINK = 'link',
}

export enum DiscountType {
    PERCENT = 'percent',
    FIXED = 'fixed',
}

// =============================================================================
// CORE DATA INTERFACES
// =============================================================================

// Store configuration data - all fields are optional as they can be undefined in API responses
export interface StoreData {
    theme: StoreTheme;
    name?: string;
    primary_color?: string;
    secondary_color?: string;
    primary_font?: string;
    checkout_image?: string;
    checkout_heading?: string;
    checkout_title?: string;
    checkout_button_text?: string;
    legacy_funnel?: boolean;
}

// Section data - flexible content structure
export interface SectionData {
    title?: string;
    description?: string;
    layout?: string;
    content?: string;
    image?: string;
    background_color?: string;
    text_color?: string;
}

export interface Section {
    section_id: number;
    store_id: number;
    status: number;
    order: number;
    data: SectionData;
}

// =============================================================================
// PRODUCT-SPECIFIC INTERFACES
// =============================================================================

export interface DiscountCode {
    code: string;
    type: DiscountType;
    value: number;
    description?: string;
    expiry_date?: string;
    usage_limit?: number;
    used_count?: number;
}

export interface StanFlexiblePayment {
    enabled: boolean;
    deposit_percentage?: number;
    installments?: number;
}

export interface PriceOptions {
    amount: number;
    currency: string;
    interval?: PaymentInterval;
    label?: string;
}

export interface Price {
    amount: number;
    sale_amount?: number;
    sale_amount_available?: boolean;
    currency: string;
    interval?: PaymentInterval;
    discount_codes_available?: boolean;
    // Note: discount_codes gets removed in dict_for_public, so not included for client
    membership_duration_available?: boolean;
    membership_duration?: number;
    membership_id?: number;
    stan_flexible_payment?: StanFlexiblePayment;
    price_options?: Record<string, PriceOptions>;
}

export interface QuestionOption {
    value: string;
    id?: string;
}

export interface Question {
    required: boolean;
    content: string;
    type: 'name' | 'email' | 'phone' | 'text' | 'radio' | 'dropdown' | 'checkbox';
    options?: QuestionOption[];
}

export interface Review {
    id?: string;
    name: string;
    description: string; // This is 'description' in the schema, not 'review'
    rating: number;
    image?: string;
}

export interface DigitalAsset {
    name: string;
    url: string;
    size?: number;
    type?: string;
    file_id?: string;
}

export interface MeetingReminderTiming {
    timing: number;
    unit: 'minutes' | 'hours' | 'days';
}

export interface MeetingReminder {
    enabled: boolean;
    timings: MeetingReminderTiming[];
}

export interface MeetingData {
    meeting_id?: number;
    description?: string;
    duration_minutes: number;
    price?: Price;
    timezone: string;
    slug?: string;
    meeting_prep_hours?: number;
    availability: Record<number, string[][]>; // day of week -> [start, end] time pairs
    questions?: Question[];
    type: BookingType.ONE_ON_ONE | BookingType.GROUP;
    platform?: string;
    attendees_limit: number;
    before_buffer_time?: number;
    after_buffer_time?: number;
    book_within_the_next_n_days?: number;
    reminders?: MeetingReminder;
}

export interface WebinarSession {
    date: string; // YYYY-MM-DD format
    time: string; // HH:MM format
    duration_minutes?: number;
}

export interface WebinarData {
    meeting_id?: number;
    description?: string;
    duration_minutes: number;
    price?: Price;
    timezone: string;
    slug?: string;
    sessions: WebinarSession[];
    type: BookingType.WEBINAR;
    platform?: string;
    attendees_limit: number;
    reminders?: MeetingReminder;
}

export interface DigitalDownloadData {
    digital_assets_type: DigitalAssetType;
    digital_assets?: DigitalAsset[];
    title?: string;
    description?: string;
    price?: Price;
}

export interface MarketingPageData {
    type: string;
    html?: string;
    grapesjs?: Record<string, unknown>; // GrapesJS editor data
}

export interface FulfillmentData {
    shipping_required?: boolean;
    weight?: number;
    dimensions?: {
        length: number;
        width: number;
        height: number;
    };
    tracking_enabled?: boolean;
}

export interface LeadMagnetData {
    digital_assets_type: DigitalAssetType;
    digital_assets?: DigitalAsset[];
    title?: string;
    questions?: Question[];
}

export interface CommunityIntegrationData {
    enabled: boolean;
    community_id?: number;
}

export interface MembershipData {
    stripe_product_id?: string;
    access_duration?: number;
    access_type?: string;
    community_integration?: CommunityIntegrationData;
    recurring_billing?: boolean;
}

export interface LinkData {
    url: string;
    open_in_new_tab?: boolean;
}

export interface FunnelPageData {
    reference_page_id?: number;
}

export interface LessonData {
    title: string;
    description?: string;
    video?: string;
    digital_assets?: DigitalAsset[];
    duration_minutes?: number;
    preview?: boolean;
}

export interface Lesson {
    lesson_id: number;
    module_id: number;
    status: number;
    order: number;
    data: LessonData;
}

export interface DripSettings {
    days: number;
    enabled?: boolean;
}

export interface ModuleData {
    title: string;
    description?: string;
    drip_settings?: DripSettings;
}

export interface Module {
    module_id: number;
    status: ModuleStatus;
    order: number;
    data: ModuleData;
    lessons: Lesson[];
}

export interface CourseTheme {
    highlight_color?: string;
    background_color?: string;
    title_font?: string;
}

export interface CourseDataInner {
    title: string;
    description?: string;
    banner_image_url?: string;
    theme?: CourseTheme;
}

export interface CourseData {
    data: CourseDataInner;
    modules?: Module[];
}

export interface AvatarDataInner {
    name?: string;
    description?: string;
    personality?: string;
    avatar_image?: string;
    voice_settings?: {
        voice_id?: string;
        speed?: number;
        pitch?: number;
    };
    training_data?: string;
}

export interface AvatarData {
    data: AvatarDataInner;
}

export interface InventoryData {
    stock?: number;
    manage?: boolean;
    allow_backorders?: boolean;
    low_stock_amount?: number;
}

export interface AffiliateShare {
    user_id: number;
    percentage: number;
    name?: string;
    email?: string;
}

export interface AffiliateTermsAndCondition {
    enabled: boolean;
    text: string;
    agreed_at?: string;
}

export interface ResellRights {
    affiliate_terms_and_condition?: AffiliateTermsAndCondition;
    percentage?: number;
    enabled?: boolean;
}

export interface CountdownTimer {
    end_date?: string;
    show_timer?: boolean;
    timezone?: string;
    expired_message?: string;
}

export interface CurrencyOverride {
    currency?: string;
}

export interface MailchimpIntegration {
    list_id?: string;
    api_key?: string;
    enabled?: boolean;
}

export interface CommunityIntegration {
    discord_server_id?: string;
    telegram_group_id?: string;
    slack_workspace_id?: string;
    enabled?: boolean;
}

export interface SecondProduct {
    enabled: boolean;
    page_id?: number;
    discount_percentage?: number;
}

// =============================================================================
// MAIN PRODUCT INTERFACE
// =============================================================================

// TODO Merge or remove
export interface Product {
    id?: number;
    title: string;
    description?: string;
    image?: string;
    type: PageType;
    price?: Price;
    questions?: Question[];
    reviews?: Review[];
    slug?: string;
    url?: string; // Added by buildProducts
    tag?: string; // Added by buildProducts

    // Product type-specific data - only one should be present based on type
    meeting?: MeetingData;
    webinar?: WebinarData;
    digital_download?: DigitalDownloadData;
    marketing_page?: MarketingPageData;
    fulfillment?: FulfillmentData;
    lead_magnet?: LeadMagnetData;
    membership?: MembershipData;
    ai_avatar?: AvatarData;
    link?: LinkData;
    course?: CourseData;
    funnel_page?: FunnelPageData;

    inventory?: InventoryData;

    // Integration data
    emails?: string[]; // Email addresses for notifications
    mailchimp_integration?: MailchimpIntegration;
    payment_methods?: Record<string, boolean>; // e.g., { "stripe": true, "paypal": false }
}

// TODO: Verify
export interface ProductButton {
    layout: string;
    button_text?: string;
    color?: string;
    background_color?: string;
    border_radius?: number;
    font_size?: number;
    image?: string;
}

export const BUTTON_LAYOUTS = {
    BUTTON: 'button',
    CALLOUT: 'callout',
    PREVIEW: 'preview',
} as const;

export type ButtonLayout = (typeof BUTTON_LAYOUTS)[keyof typeof BUTTON_LAYOUTS];

// =============================================================================
// PAGE DATA INTERFACE
// =============================================================================

// TODO MErge or remove
export interface PageData {
    primary_color?: string;
    secondary_color?: string;
    bottom_title?: string;
    video?: string;
    top_banner_text?: string | null;
    product: Product;
    button?: ProductButton;
    submit_title?: string;
    second_built_in_product?: SecondProduct;
    community_integration?: CommunityIntegration;
    location?: PageLocation;
    affiliate_shares?: AffiliateShare[];
    resell_rights?: ResellRights;
    countdown_timer?: CountdownTimer;
    currency_override?: CurrencyOverride;
    product_generation_ulid?: string;
    funnel_page_id?: number; // Added when type is FUNNEL_PAGE
}

// =============================================================================
// PAGE INTERFACE
// =============================================================================

export interface Page {
    page_id: number;
    store_id: number;
    type: PageType;
    status: PageStatus;
    slug?: string | null;
    order: number;
    data: ServerPageData;
    created_at?: number; // Unix timestamp
    updated_at?: number; // Unix timestamp
    url?: string; // Added by the API for convenience
    storeName?: string; // Added by BuildProductList
}

// =============================================================================
// MAIN STORE INTERFACE
// =============================================================================

export interface Store {
    store_id: number;
    type: StoreType;
    status: number;
    slug: string;
    data: StoreData;
    sections: Section[];
    pages: Page[];

    // Additional fields added by the API
    first_page_type?: PageType | null;
    first_page_title?: string | null;
}

// =============================================================================
// API RESPONSE INTERFACES
// =============================================================================

export interface StoreResponse {
    store: Store;
}
